import { useState, useEffect } from 'react';

import NavigationMenu from './components/layout/NavigationMenu';
import Footer from './components/layout/Footer';

import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import AboutOneomicsPage from './pages/AboutOneomicsPage';
import HowItWorksPage from './pages/HowItWorksPage';
import AnalysisPage from './pages/AnalysisPage';
import ResultsPage from './pages/ResultsPage';
import FAQsPage from './pages/FAQsPage';
import FeedbackPage from './pages/FeedbackPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { isAuthenticated, getAuthUser, clearAuthSession } from './api/authApi';

// Tabs that require a logged-in session. Add 'tools' here once ToolsPage exists
// and has a case below — right now there's no ToolsPage import/case at all,
// so the nav link exists but clicking it just falls through to Home regardless.
const PROTECTED_TABS = ['analysis', 'results'];

const App = () => {
  // Lazy initial state: if someone arrives via the password-reset email link
  // (?resetToken=...), land directly on the reset-password page with the token
  // captured, instead of Home. This app has no router, so a plain query param
  // on the root URL is how that link communicates with the SPA.
  const [resetToken] = useState(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('resetToken');
  });
  const [activeTab, setActiveTab] = useState(() => (resetToken ? 'reset-password' : 'home'));

  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  // Read fresh on every render rather than caching in state — login/register/logout
  // all call setActiveTab(...) on success, which re-renders App anyway, so this
  // picks up the change with no extra state needed.
  const loggedIn = isAuthenticated();

  const handleLogout = () => {
    clearAuthSession();
    setActiveTab('home');
  };

  const renderPage = () => {
   if (PROTECTED_TABS.includes(activeTab) && !loggedIn) {
    // Pass the intended destination so LoginPage can redirect there after login
    return <LoginPage setActiveTab={setActiveTab} redirectTab={activeTab} />;
  }

    switch (activeTab) {
      case 'home': return <HomePage setActiveTab={setActiveTab} />;
      case 'about': return <AboutPage />;
      case 'about-oneomics': return <AboutOneomicsPage />;
      case 'howit-works': return <HowItWorksPage />;
      case 'analysis': return <AnalysisPage setActiveTab={setActiveTab} />;
      case 'results': return <ResultsPage setActiveTab={setActiveTab} />;
      case 'faqs': return <FAQsPage />;
      case 'feedback': return <FeedbackPage />;
      case 'login': return <LoginPage setActiveTab={setActiveTab} />;
      case 'register': return <RegisterPage setActiveTab={setActiveTab} />;
      case 'forgot-password': return <ForgotPasswordPage setActiveTab={setActiveTab} />;
      case 'reset-password': return <ResetPasswordPage token={resetToken} setActiveTab={setActiveTab} />;
      default: return <HomePage setActiveTab={setActiveTab} />;
    }
  };

  return (
    <div className="app-root relative min-h-screen text-slate-800">
      <div className="relative z-10">
        <NavigationMenu
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isAuthenticated={loggedIn}
          user={getAuthUser()}
          onLogout={handleLogout}
        />
        <main className="min-h-screen">{renderPage()}</main>
        <Footer setActiveTab={setActiveTab} isAuthenticated={loggedIn} />
      </div>
    </div>
  );
};

export default App;